import { Node } from 'reactflow';
import {
  CANVAS_HEIGHT_PADDING,
  COLLAPSED_LAYER_HEIGHT,
  LAYER_HEIGHT,
  NodesTypes,
  SUB_LAYER_PADDING_Y,
} from '@constants/canvas/general';
import {
  LayerSubLayers,
  LayerTypes,
  ModeLayers,
  Modes,
  SubLayerTypes,
} from '@constants/canvas/layers';
import NodesRow from '@utils/canvas/NodesRow';

export const SUB_LAYER_FULL_PADDING_Y = SUB_LAYER_PADDING_Y * 2;

export const SUB_LAYER_MIN_HEIGHT = NodesRow.Height + SUB_LAYER_FULL_PADDING_Y;

export const MIN_ROWS_COUNT = 1;

export type IsCollapsed = (id: LayerTypes | SubLayerTypes) => boolean;

export type CanvasGeometry = {
  subLayerHeights: Record<string, number>;
  subLayerPositions: Record<string, number>;
  layerHeights: Record<string, number>;
  layerPositions: Record<string, number>;
  canvasHeight: number;
};

/**
 * Rows a sublayer must reserve, derived from the lowest occupied row among its
 * children. A sublayer always reserves at least one row, even when empty.
 */
export const getRowsCount = (
  nodes: readonly Node[],
  subLayerId: string,
): number => {
  const lastRowIndex = nodes.reduce((max, node) => {
    if (node.parentNode !== subLayerId) return max;

    const rowIndex = Math.floor(node.position.y / NodesRow.Height);

    return rowIndex > max ? rowIndex : max;
  }, -1);

  return Math.max(lastRowIndex + 1, MIN_ROWS_COUNT);
};

/**
 * Rendered height of one sublayer, folding in the collapsed case.
 */
export const getSubLayerHeight = (
  nodes: readonly Node[],
  subLayerId: SubLayerTypes,
  collapsed: boolean,
): number => {
  if (collapsed) return COLLAPSED_LAYER_HEIGHT;

  return (
    getRowsCount(nodes, subLayerId) * NodesRow.Height + SUB_LAYER_FULL_PADDING_Y
  );
};

/**
 * Full canvas geometry for a mode, derived from node data alone. Each layer
 * opens with its title block, its sublayers stack beneath it, and each layer's
 * height feeds the offset of the next.
 */
export const computeCanvasGeometry = (
  nodes: readonly Node[],
  mode: Modes,
  isCollapsed: IsCollapsed,
): CanvasGeometry => {
  const subLayerHeights: Record<string, number> = {};
  const subLayerPositions: Record<string, number> = {};
  const layerHeights: Record<string, number> = {};
  const layerPositions: Record<string, number> = {};

  let topLayerHeight = 0;

  (ModeLayers[mode] as readonly LayerTypes[]).forEach((layerId) => {
    let topSubLayerHeight = LAYER_HEIGHT;
    let subLayersHeight = 0;

    LayerSubLayers[layerId].forEach((id) => {
      const subLayerId = id as SubLayerTypes;

      const height = getSubLayerHeight(
        nodes,
        subLayerId,
        isCollapsed(subLayerId),
      );

      subLayerHeights[subLayerId] = height;
      subLayerPositions[subLayerId] = topLayerHeight + topSubLayerHeight;

      topSubLayerHeight += height;
      subLayersHeight += height;
    });

    layerHeights[layerId] = isCollapsed(layerId)
      ? COLLAPSED_LAYER_HEIGHT
      : subLayersHeight + LAYER_HEIGHT;
    layerPositions[layerId] = topLayerHeight;

    topLayerHeight += layerHeights[layerId];
  });

  return {
    subLayerHeights,
    subLayerPositions,
    layerHeights,
    layerPositions,
    canvasHeight: topLayerHeight + CANVAS_HEIGHT_PADDING,
  };
};

/**
 * Stamps computed geometry onto the layer and sublayer container nodes, leaving
 * entity nodes referentially untouched. Layer components take `$height` as a css
 * string and sublayer components as a number, so each is written in the shape
 * its styled component expects.
 */
export const applyCanvasGeometry = (
  nodes: Node[],
  geometry: CanvasGeometry,
): Node[] =>
  nodes.map((node) => {
    const isLayer = node.type === NodesTypes.LayerNode;
    const isSubLayer = node.type === NodesTypes.SubLayerNode;

    if (!isLayer && !isSubLayer) return node;

    const height = isLayer
      ? geometry.layerHeights[node.id]
      : geometry.subLayerHeights[node.id];

    const y = isLayer
      ? geometry.layerPositions[node.id]
      : geometry.subLayerPositions[node.id];

    if (height === undefined || y === undefined) return node;

    return {
      ...node,
      position: { ...node.position, y },
      data: { ...node.data, height: isLayer ? `${height}px` : height },
    };
  });
